import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
// New icons for maximize/minimize
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);
const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);
const AutofillIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const API_URL = '/api';

// BOM Type options
const BOM_TYPE_OPTIONS = [
  { value: 'Production BOM', label: 'Production BOM' },
  { value: 'Assembly BOM', label: 'Assembly BOM' },
  { value: 'Packing BOM', label: 'Packing BOM' },
];

// Status options
const STATUS_OPTIONS = [
  { value: 'New', label: 'New' },
  { value: 'Development', label: 'Development' },
  { value: 'Certified', label: 'Certified' },
];

// Material Type options
const MATERIAL_TYPE_OPTIONS = [
  { value: 'Input', label: 'Input' },
  { value: 'Byproduct', label: 'Byproduct' },
];

// Flush Type options
const FLUSH_TYPE_OPTIONS = [
  { value: 'Manual', label: 'Manual' },
  { value: 'Forward', label: 'Forward' },
  { value: 'Backward', label: 'Backward' },
];

const initialHeader = {
  bomType: 'Production BOM',
  bomCode: '',
  name: '',
  status: 'New',
  bomQty: '',
  department_id: "", // Add department_id field
};

const initialItem = {
  materialType: 'Input',
  productCode: '',
  productName: '',
  uom: '',
  qty: '',
  flushType: 'Manual',
  department_id: "", // Add department_id field
};

// ERP Command Constants
const ERP_COMMANDS = {
  SAVE_POPUP_ITEMS: 'SAVE_POPUP_ITEMS',
  LOAD_SAVED_ITEMS: 'LOAD_SAVED_ITEMS',
  DELETE_SAVED_ITEMS: 'DELETE_SAVED_ITEMS',
  AUTOFILL_ITEMS: 'AUTOFILL_ITEMS' // New command for autofilling items
};

// Multi-Select Product Modal Component
const ProductMultiSelectModal = ({ 
  open, 
  onClose, 
  products, 
  onConfirm,
  onSaveToTable,
  onAutofill 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const tableBodyRef = useRef(null);

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(product => {
      // Search in all specified fields
      return (
        (product.ProductCode && product.ProductCode.toLowerCase().includes(term)) || 
        (product.ProductName && product.ProductName.toLowerCase().includes(term)) ||
        (product.Status && product.Status.toLowerCase().includes(term)) ||
        (product.CategoryName && product.CategoryName.toLowerCase().includes(term)) ||
        (product.UOMName && product.UOMName.toLowerCase().includes(term)) ||
        (product.Description && product.Description.toLowerCase().includes(term)) ||
        (product.BranchName && product.BranchName.toLowerCase().includes(term)) ||
        (product.DepartmentName && product.DepartmentName.toLowerCase().includes(term)) ||
        (product.AllowProductNegativeInventory !== undefined && 
         (product.AllowProductNegativeInventory ? 'yes' : 'no').includes(term)) ||
        (product.AllowBatchOnProduct !== undefined && 
         (product.AllowBatchOnProduct ? 'yes' : 'no').includes(term)) ||
        (product.ProductType && product.ProductType.toLowerCase().includes(term)) ||
        (product.ProductionBOMType && product.ProductionBOMType.toLowerCase().includes(term))
      );
    });
  }, [products, searchTerm]);

  // Check if all filtered products are selected
  const allFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.every(product => selectedProducts.includes(product.ProductCode));
  }, [filteredProducts, selectedProducts]);

  // Check if some filtered products are selected (for indeterminate state)
  const someFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.some(product => selectedProducts.includes(product.ProductCode)) && !allFilteredSelected;
  }, [filteredProducts, selectedProducts, allFilteredSelected]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && tableBodyRef.current && highlightedIndex >= 0) {
      const items = tableBodyRef.current.querySelectorAll('.product-row');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, open]);

  // Reset and focus when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedProducts([]);
      setHighlightedIndex(-1);
      
      // Focus search input after a short delay
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Handle search input keys
    if (e.target === searchInputRef.current) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(0);
        // Move focus to the table body
        setTimeout(() => {
          if (tableBodyRef.current) {
            tableBodyRef.current.focus();
          }
        }, 0);
        return;
      }
      return;
    }

    // Handle table body navigation
    if (e.target === tableBodyRef.current) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => {
            if (prev < filteredProducts.length - 1) {
              return prev + 1;
            } else if (filteredProducts.length > 0) {
              return 0; // Loop back to first item
            }
            return prev;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => {
            if (prev > 0) {
              return prev - 1;
            } else if (filteredProducts.length > 0) {
              return filteredProducts.length - 1; // Loop to last item
            }
            return prev;
          });
          break;
        case ' ':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
            toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (e.ctrlKey) {
            // Ctrl+Enter for autofill
            handleAutofill();
          } else if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
            // Regular Enter for selecting highlighted product
            toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
          }
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    }
  };

  // Toggle product selection
  const toggleProductSelection = (productCode) => {
    setSelectedProducts(prev => 
      prev.includes(productCode)
        ? prev.filter(code => code !== productCode)
        : [...prev, productCode]
    );
  };

  // Handle select all filtered products
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Add all filtered products to selection
      const allProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => [...new Set([...prev, ...allProductCodes])]);
    } else {
      // Remove all filtered products from selection
      const filteredProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => prev.filter(code => !filteredProductCodes.includes(code)));
    }
  };

  // Handle clear all selection
  const handleClearAll = () => {
    setSelectedProducts([]);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    onConfirm(selectedProducts);
    onClose();
  };

  // Handle save to table
  const handleSaveToTable = async () => {
    if (selectedProducts.length === 0) return;
    
    setIsSaving(true);
    try {
      await onSaveToTable(selectedProducts);
      alert('Items saved to table successfully!');
    } catch (error) {
      console.error('Error saving to table:', error);
      alert('Failed to save items to table');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle autofill
  const handleAutofill = () => {
    if (selectedProducts.length === 0) return;
    
    try {
      const result = onAutofill(selectedProducts);
      
      if (result && result.success) {
        // Success
      } else {
        alert(result?.error || 'Error autofilling table. Please try again.');
      }
    } catch (error) {
      console.error('Error during autofill:', error);
      alert('Error autofilling table. Please try again.');
    } finally {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full h-[100vh] flex flex-col border border-gray-200">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">Select Multiple Products</h3>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-200"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-5 border-b border-gray-200 bg-white">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by Product Code, Name, Status, Category, UOM, Description, Branch, Department, etc..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="absolute right-3 top-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
            <div className="font-medium text-blue-800 mb-1">Keyboard Shortcuts:</div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↑</kbd>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↓</kbd>
                <span className="ml-1">Navigate</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Space</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Ctrl</kbd>
                <span className="mx-1">+</span>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Autofill</span>
              </span>
            </div>
          </div>
        </div>
        
        <div 
          ref={listRef}
          className="flex-grow overflow-y-auto bg-gray-50 relative"
        >
          {filteredProducts.length > 0 ? (
            <div className="h-full flex flex-col">
              {/* Single Table with Sticky Header */}
              <div className="flex-grow overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {/* Sticky Header */}
                  <thead className="sticky top-0 z-30 bg-gray-100 shadow-md">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-12">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          checked={allFilteredSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someFilteredSelected;
                            }
                          }}
                          onChange={handleSelectAll}
                          title={allFilteredSelected ? "Deselect all" : someFilteredSelected ? "Select all filtered" : "Select all filtered"}
                        />
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Product Code
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[200px]">
                        Product Name
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Category
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-20">
                        UOM
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[180px]">
                        Description
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Branch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Department
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Negative Inv.
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Batch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Product Type
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        BOM Type
                      </th>
                    </tr>
                  </thead>
                  
                  {/* Scrollable Body */}
                  <tbody 
                    ref={tableBodyRef}
                    className="bg-white divide-y divide-gray-200 focus:outline-none"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                  >
                    {filteredProducts.map((product, index) => (
                      <tr
                        key={product.ProductCode}
                        className={`product-row hover:bg-blue-50 transition-colors ${
                          highlightedIndex === index ? 'bg-blue-100' : ''
                        } ${selectedProducts.includes(product.ProductCode) ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleProductSelection(product.ProductCode)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                            checked={selectedProducts.includes(product.ProductCode)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 w-32">
                          {product.ProductCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium min-w-[200px]">
                          {product.ProductName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            product.Status === 'Active' ? 'bg-green-100 text-green-800' : 
                            product.Status === 'Inactive' ? 'bg-red-100 text-red-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {product.Status || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.CategoryName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-20">
                          {product.UOMName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate min-w-[180px]" title={product.Description || ''}>
                          {product.Description || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.BranchName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.DepartmentName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-32">
                          {product.AllowProductNegativeInventory !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowProductNegativeInventory ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowProductNegativeInventory ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          {product.AllowBatchOnProduct !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowBatchOnProduct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowBatchOnProduct ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductType || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductionBOMType || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-16">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No products found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {products.length === 0 
                  ? 'No products are available in the system.' 
                  : 'No products match your search criteria. Try different keywords.'}
              </p>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{selectedProducts.length}</span> product{selectedProducts.length !== 1 ? 's' : ''} selected
            </div>
            {selectedProducts.length > 0 && (
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleSaveToTable}
              disabled={selectedProducts.length === 0 || isSaving}
            >
              <SaveIcon className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save to Table'}
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleAutofill}
              disabled={selectedProducts.length === 0}
            >
              <AutofillIcon className="mr-2 h-4 w-4" />
              Autofill Table
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              onClick={handleConfirm}
              disabled={selectedProducts.length === 0}
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Searchable Dropdown Component
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const secondaryLabel = getOptionSecondaryLabel(option).toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

const BOMFormModal = ({ open, onClose, onSubmit, initialData, isEdit, formData }) => {
  const [header, setHeader] = useState(initialHeader);
  const [items, setItems] = useState([{ ...initialItem }]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    products: [],
    nextBomNumber: '',
  });
  
  // Added state for user management
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // New state for maximize/minimize functionality
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Department-related state (matching StockIn implementation)
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);
  
  // Multi-select modals state
  const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
  const [savedItems, setSavedItems] = useState([]); // State for saved items from table
  
  // Function to fetch current user
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        console.log('Current user fetched:', userData);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch departments (matching StockIn implementation)
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`);
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration (matching StockIn implementation)
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`);
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the BOM screen configuration
      const bomScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'BOM'.toLowerCase()
      );
      
      console.log('BOM screen configuration:', bomScreen);
      if (bomScreen) {
        setScreenConfig(bomScreen);
        // Show department field if ShowOnHeader is true
        const shouldShowDepartment = bomScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // Show department item field if ShowOnItem is true
        const shouldShowDepartmentItem = bomScreen.ShowOnItem === true;
        setShowDepartmentItemField(shouldShowDepartmentItem);
        
        // If department fields should be shown, fetch departments
        if (shouldShowDepartment || shouldShowDepartmentItem) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
      setShowDepartmentItemField(false);
    }
  };
  
  // Toggle maximize/minimize function
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  // ERP Command: Save popup items to table
  const executeERPCommand = async (command, data) => {
    try {
      const response = await fetch(`${API_URL}/erp/execute-command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          command,
          data
        })
      });
      
      if (!response.ok) {
        throw new Error(`ERP command failed: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error executing ERP command ${command}:`, error);
      throw error;
    }
  };
  
  // Save selected products to table
  const savePopupItemsToTable = async (selectedProductCodes) => {
    const productsToSave = selectedProductCodes.map(productCode => {
      const product = dropdownData.products.find(p => p.ProductCode === productCode);
      return {
        productCode: product.ProductCode,
        productName: product.ProductName,
        uom: product.UOMName || product.UOMID || '',
        savedDate: new Date().toISOString()
      };
    });
    
    return await executeERPCommand(ERP_COMMANDS.SAVE_POPUP_ITEMS, {
      bomNumber: header.bomCode,
      items: productsToSave
    });
  };
  
  // Load saved items from table
  const loadSavedItemsFromTable = async () => {
    try {
      const result = await executeERPCommand(ERP_COMMANDS.LOAD_SAVED_ITEMS, {
        bomNumber: header.bomCode
      });
      
      if (result && result.items && result.items.length > 0) {
        setSavedItems(result.items);
        
        // Convert saved items to form items
        const formItems = result.items.map(savedItem => ({
          ...initialItem,
          productCode: savedItem.productCode,
          productName: savedItem.productName,
          uom: savedItem.uom,
          savedToTable: true
        }));
        
        setItems(formItems);
      }
    } catch (error) {
      console.error('Error loading saved items:', error);
    }
  };
  
  // Delete saved items from table
  const deleteSavedItemsFromTable = async () => {
    try {
      await executeERPCommand(ERP_COMMANDS.DELETE_SAVED_ITEMS, {
        bomNumber: header.bomCode
      });
      setSavedItems([]);
    } catch (error) {
      console.error('Error deleting saved items:', error);
    }
  };
  
  // Autofill selected products into the table
 // In the autofillSelectedProducts function in BOMFormModal:
const autofillSelectedProducts = (selectedProductCodes) => {
  if (selectedProductCodes.length === 0) return;
  
  try {
    const newItems = selectedProductCodes.map(productCode => {
      const product = dropdownData.products.find(p => p.ProductCode === productCode);
      if (!product) return { ...initialItem };
      
      return {
        ...initialItem,
        productCode: product.ProductCode,
        productName: product.ProductName,
        uom: product.UOMName || '', // Use UOMName (string) instead of UOMID (number)
        department_id: showDepartmentItemField ? "" : undefined,
      };
    });
    
    // Append new items to existing items instead of replacing them
    setItems(prevItems => [...prevItems, ...newItems]);
    
    // Return success for the modal to show notification
    return { success: true, count: selectedProductCodes.length };
  } catch (error) {
    console.error('Error in autofillSelectedProducts:', error);
    // Return error for the modal to show error message
    return { success: false, error: error.message };
  }
};
  
  // Handle multi-select products
// In the handleMultiSelectProducts function in BOMFormModal:
const handleMultiSelectProducts = (selectedProductCodes) => {
  if (selectedProductCodes.length === 0) return;
  
  const newItems = selectedProductCodes.map(productCode => {
    const product = dropdownData.products.find(p => p.ProductCode === productCode);
    if (!product) return { ...initialItem };
    
    return {
      ...initialItem,
      productCode: product.ProductCode,
      productName: product.ProductName,
      uom: product.UOMName || '', // Use UOMName (string) instead of UOMID (number)
      department_id: showDepartmentItemField ? "" : undefined,
    };
  });
  
  // Append new items to existing items instead of replacing them
  setItems(prevItems => [...prevItems, ...newItems]);
};
  
  // Handle keyboard shortcuts for multi-select modals
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      // Check for Alt+P - Multi-select products
      if (e.altKey && e.key === 'p') {
        e.preventDefault();
        setShowMultiSelectModal(true);
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);
  
  useEffect(() => {
    if (open) {
      // Reset maximize state when modal opens
      setIsMaximized(false);
      
      // Fetch current user when modal opens
      fetchCurrentUser();
      fetchScreenConfig(); // Fetch screen config
      
      const initializeForm = async () => {
        try {
          // Try to get user from token
          let currentUser = null;
          try {
            const token = localStorage.getItem('authToken');
            if (!token) {
              throw new Error('No authentication token found');
            }
            
            // Decode the JWT token
            const parts = token.split('.');
            if (parts.length !== 3) {
              throw new Error('Invalid token format');
            }
            
            // Decode the payload
            const payload = JSON.parse(atob(parts[1]));
            
            // Extract user information from the payload
            currentUser = {
              UserID: payload.userId,
              UserName: payload.email
            };
          } catch (error) {
            console.error('Error extracting user from token:', error);
          }
          
          if (!currentUser || !currentUser.UserID) {
            console.error('Could not extract user information from token');
            alert('You must be logged in to create BOM records');
            onClose();
            return;
          }
          
          // Fetch additional user details from the API
          try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_URL}/users/${currentUser.UserID}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const userDetails = await response.json();
              console.log('Fetched user details:', userDetails);
              
              // Update with the actual username from the database
              currentUser.UserName = userDetails.Username || userDetails.FullName || userDetails.Email;
            }
          } catch (error) {
            console.error('Error fetching user details:', error);
          }
          
          // If we have user info, proceed with form initialization
          console.log('User authenticated:', currentUser);
          
          // Use formData from props if available, otherwise fetch it
          if (formData && formData.products && formData.products.length > 0) {
            setDropdownData({
              products: formData.products || [],
              nextBomNumber: formData.nextBomNumber || '',
            });
          } else {
            await fetchDropdownData();
          }
          
          // Load saved items from table for new records
          if (!isEdit) {
            loadSavedItemsFromTable();
          }
          
          if (isEdit && initialData) {
            // Debug logging
            console.log('Initial data received:', initialData);
            
            // Set header data for edit mode
            setHeader({
              bomType: initialData.BOMType || 'Production BOM',
              bomCode: initialData.BOMID || '',
              name: initialData.BOMName || '',
              status: initialData.Status || 'New',
              bomQty: initialData.BOMQty?.toString() || '',
              department_id: initialData.DepartmentID?.toString() || "", // Add department
            });
            
            // Set items data
            if (initialData.Details && initialData.Details.length > 0) {
              setItems(initialData.Details.map(item => ({
                materialType: item.MaterialType || 'Input',
                productCode: item.ProductCode || '',
                productName: item.ProductName || '',
                uom: item.UOM || '',
                qty: item.Qty?.toString() || '',
                flushType: item.FlushType || 'Manual',
                department_id: item.DepartmentID?.toString() || "" // Add department
              })));
            } else {
              setItems([{ ...initialItem }]);
            }
          } else {
            // For new records
            setHeader({ 
              ...initialHeader, 
              bomCode: formData?.nextBomNumber || '',
              department_id: "" // Initialize empty department
            });
            
            // If no saved items, initialize with one empty item
            if (savedItems.length === 0) {
              setItems([{ ...initialItem }]);
            }
          }
          
          setErrors({});
          setIsSubmitting(false);
        } catch (error) {
          console.error('Error initializing form:', error);
          alert('Error initializing form. Please try again.');
          onClose();
        }
      };
      
      initializeForm();
    }
  }, [open, initialData, isEdit, onClose, formData, savedItems.length]);
  
  // Add this useEffect to monitor the header state
  useEffect(() => {
    console.log('Header state updated:', header);
  }, [header]);
  
const fetchDropdownData = async () => {
  try {
    setLoading(true);
    
    // Fetch all data in parallel
    const [
      screenConfigRes,
      productsRes,
      nextBomNumberRes,
      nextDetailNumberRes,
      statusOptionsRes,
      bomTypeOptionsRes,
      materialTypeOptionsRes,
      flushTypeOptionsRes,
      uomRes,
      departmentsRes
    ] = await Promise.all([
      fetch(`${API_URL}/bom/screen-config`),
      fetch(`${API_URL}/bom/products`),
      fetch(`${API_URL}/bom/next-number`),
      fetch(`${API_URL}/bom/next-detail-number`),
      fetch(`${API_URL}/bom/status-options`),
      fetch(`${API_URL}/bom/bom-type-options`),
      fetch(`${API_URL}/bom/material-type-options`),
      fetch(`${API_URL}/bom/flush-type-options`),
      fetch(`${API_URL}/uoms`),
      fetch(`${API_URL}/departments`)
    ]);
    
    // Parse all responses
    const [
      screenConfig,
      products,
      nextBomNumberData,
      nextDetailNumberData,
      statusOptions,
      bomTypeOptions,
      materialTypeOptions,
      flushTypeOptions,
      uomData,
      departmentsData
    ] = await Promise.all([
      screenConfigRes.json(),
      productsRes.json(),
      nextBomNumberRes.json(),
      nextDetailNumberRes.json(),
      statusOptionsRes.json(),
      bomTypeOptionsRes.json(),
      materialTypeOptionsRes.json(),
      flushTypeOptionsRes.json(),
      uomRes.json(),
      departmentsRes.json()
    ]);
    
    // Set dropdown data
    setDropdownData({
      products,
      nextBomNumber: nextBomNumberData.nextBomNumber || '',
      nextDetailNumber: nextDetailNumberData.nextDetailNumber || '',
      screenConfig,
      statusOptions,
      bomTypeOptions,
      materialTypeOptions,
      flushTypeOptions,
      uomOptions: uomData,
      departments: departmentsData
    });
    
    // Set BOM number if creating new
    if (!isEdit) {
      setHeader(prev => ({ 
        ...prev, 
        bomCode: nextBomNumberData.nextBomNumber || ''
      }));
    }
  } catch (error) {
    console.error('Error fetching dropdown data:', error);
    alert('Error loading form data');
  } finally {
    setLoading(false);
  }
};
  
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader({ ...header, [name]: value });
  };
  
  const handleItemChange = (idx, e) => {
    const { name, value } = e.target;
    const newItems = [...items];
    newItems[idx][name] = value;
    setItems(newItems);
  };
  
  // Handle product selection - auto-fill product details
// In the handleProductChange function in BOMFormModal:
const handleProductChange = (idx, productCode) => {
  const newItems = [...items];
  
  if (productCode) {
    const selectedProduct = dropdownData.products.find(p => p.ProductCode === productCode);
    if (selectedProduct) {
      // Auto-fill all product-related fields
      newItems[idx] = {
        ...newItems[idx],
        productCode: selectedProduct.ProductCode,
        productName: selectedProduct.ProductName,
        uom: selectedProduct.UOMName || '', // Use UOMName (string) instead of UOMID (number)
      };
    }
  } else {
    // Clear product-related fields if no product is selected
    newItems[idx] = {
      ...newItems[idx],
      productCode: '',
      productName: '',
      uom: '',
    };
  }
  
  setItems(newItems);
};
  
  const addItem = () => {
    setItems([...items, { 
      ...initialItem,
      department_id: showDepartmentItemField ? "" : undefined, // Only include if department field is shown
    }]);
  };
  
  const removeItem = (idx) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== idx));
    }
  };
  
  const filteredProducts = useMemo(() => {
    return dropdownData.products.filter(product => product.ProductType !== 'Finished');
  }, [dropdownData.products]);

  const validate = () => {
    const errs = {};
    if (!header.bomType) errs.bomType = 'Required';
    if (!header.bomCode || !header.bomCode.trim()) errs.bomCode = 'Required';
    if (!header.name || !header.name.trim()) errs.name = 'Required';
    if (!header.status) errs.status = 'Required';
    if (!header.bomQty) errs.bomQty = 'Required';
    
    // Validate department field only if it's shown (matching StockIn implementation)
    if (showDepartmentField && !header.department_id) errs.department_id = 'Required';
    
    items.forEach((item, idx) => {
      if (!item.materialType) errs[`materialType_${idx}`] = 'Required';
      if (!item.productCode) errs[`productCode_${idx}`] = 'Required';
      if (!item.productName) errs[`productName_${idx}`] = 'Required';
      if (!item.uom) errs[`uom_${idx}`] = 'Required';
      if (!item.qty) errs[`qty_${idx}`] = 'Required';
      if (!item.flushType) errs[`flushType_${idx}`] = 'Required';
      
      // Validate department item field only if it's shown (matching StockIn implementation)
      if (showDepartmentItemField && !item.department_id) errs[`department_id_${idx}`] = 'Required';
      
      // Check if the product is finished
      const product = dropdownData.products.find(p => p.ProductCode === item.productCode);
      if (product && product.ProductType === 'Finished') {
        errs[`productCode_${idx}`] = 'Finished products are not allowed';
      }
    });
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || isSubmitting) return;
    
    if (!isUserValid || !currentUser || !currentUser.UserID) {
      alert(authError || 'User information not available. Please refresh the page and log in again.');
      return;
    }
    
    setIsSubmitting(true);
    setLoading(true);
    
    try {
      // Convert CreatedBy to string to match database expectations
      const createdBy = String(currentUser.UserID);
      
      const payload = {
        BOMID: header.bomCode,
        BOMType: header.bomType,
        BOMName: header.name,
        Status: header.status,
        BOMQty: parseFloat(header.bomQty) || 0,
        CreatedBy: createdBy,
        UpdatedBy: createdBy, // Add UpdatedBy field
        // Only include department_id if showDepartmentField is true (matching StockIn implementation)
        ...(showDepartmentField && header.department_id ? { department_id: parseInt(header.department_id) } : {}),
        Details: items.map(item => ({
          MaterialType: item.materialType,
          ProductCode: item.productCode,
          ProductName: item.productName,
          UOM: item.uom,
          Qty: parseFloat(item.qty) || 0,
          FlushType: item.flushType,
          CreatedBy: createdBy,
          UpdatedBy: createdBy, // Add UpdatedBy field
          // Only include department_id if showDepartmentItemField is true (matching StockIn implementation)
          ...(showDepartmentItemField && item.department_id ? { department_id: parseInt(item.department_id) } : {})
        }))
      };
      
      console.log("Sending payload:", JSON.stringify(payload, null, 2));
      
      const url = isEdit 
        ? `${API_URL}/bom/${header.bomCode}`
        : `${API_URL}/bom`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Backend error:", errorData);
        throw new Error(errorData.message || 'Failed to save BOM record');
      }
      
      const result = await response.json();
      console.log("Backend response:", result);
      
      // Delete saved items from table after successful submission
      if (!isEdit) {
        await deleteSavedItemsFromTable();
      }
      
      // Call onSubmit to refresh data in parent component
      if (onSubmit) {
        onSubmit(result); // Pass the result to parent component
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving BOM record:', err);
      alert(err.message || 'Error saving BOM record');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };
  
  if (!open) return null;
  
  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          .saved-item-badge {
            display: inline-flex;
            align-items: center;
            background-color: #e6f7ff;
            color: #1890ff;
            font-size: 10px;
            padding: 2px 6px;
            border-radius: 10px;
            margin-left: 4px;
          }
          .autofill-button {
            background-color: #f0f9ff;
            color: #0284c7;
            border-color: #0284c7;
          }
          .autofill-button:hover {
            background-color: #e0f2fe;
          }
          .shortcut-key {
            display: inline-block;
            background-color: #f1f5f9;
            color: #334155;
            font-family: monospace;
            font-size: 0.75rem;
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
            border: 1px solid #e2e8f0;
            margin: 0 0.125rem;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      } ${
        isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'
      }`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit BOM' : 'Create BOM'}</h2>
            <div className="flex items-center gap-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
          
          {/* <div className="mt-4 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
            <div className="font-medium mb-1">Keyboard Shortcuts:</div>
            <div className="grid grid-cols-2 gap-1">
              <div><span className="shortcut-key">Alt+P</span> - Multi-select products</div>
            </div>
            <div className="mt-2 font-medium">Multi-Select Modal Commands:</div>
            <div><span className="shortcut-key">Ctrl+Enter</span> - Autofill table with selected products</div>
          </div> */}
        </div>
        
        {/* User validation warning */}
        {open && !isUserValid && (
          <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  {authError || 'User information not available. Please refresh the page and log in again.'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Scrollable Content Area */}
        <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
          <form onSubmit={handleSubmit} className="space-y-6 pb-6">
            {/* BOM Header */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">BOM Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">BOM Type *</label>
                  <select 
                    name="bomType" 
                    value={header.bomType} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 ${errors.bomType ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  >
                    {BOM_TYPE_OPTIONS.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {errors.bomType && <span className="text-xs text-red-500">{errors.bomType}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">BOM Code *</label>
                  <input 
                    name="bomCode" 
                    value={header.bomCode} 
                    onChange={handleHeaderChange} 
                    disabled
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                  />
                  {errors.bomCode && <span className="text-xs text-red-500">{errors.bomCode}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">BOM Name *</label>
                  <input 
                    name="name" 
                    value={header.name} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 ${errors.name ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  />
                  {errors.name && <span className="text-xs text-red-500">{errors.name}</span>}
                </div>
 <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Status *</label>
                  <select 
                    name="status" 
                    value={header.status} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 ${errors.status ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {errors.status && <span className="text-xs text-red-500">{errors.status}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">BOM Qty *</label>
                  <input 
                    name="bomQty" 
                    type="number" 
                    min="0" 
                    step="0.01"
                    value={header.bomQty} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 ${errors.bomQty ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  />
                  {errors.bomQty && <span className="text-xs text-red-500">{errors.bomQty}</span>}
                </div>
                
                {/* Department Selection Field - Only shown if ShowOnHeader is true (matching StockIn implementation) */}
                {showDepartmentField && (
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Department *
                    </label>
                    <SearchableDropdown
                      options={departments}
                      value={header.department_id}
                      onChange={(department_id) => setHeader(prev => ({ ...prev, department_id }))}
                      getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
                      getOptionValue={(dept) => dept.DepartmentID.toString()}
                      placeholder="Select Department"
                      error={errors.department_id}
                      disabled={loadingDepartments}
                      className="data-department-dropdown"
                    />
                    {errors.department_id && (
                      <span className="text-xs text-red-500">
                        {errors.department_id}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* BOM Items */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#384959]">BOM Materials</h3>
                <div className="flex gap-2">
                  {savedItems.length > 0 && (
                    <button 
                      type="button" 
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm flex items-center"
                      onClick={loadSavedItemsFromTable}
                      title="Load saved items from table"
                    >
                      <SaveIcon className="mr-1" size={12} /> Reload Saved
                    </button>
                  )}
                  <button 
                    type="button" 
                    className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors" 
                    onClick={addItem}
                  >
                    <PlusIcon className="mr-1" /> Add Material
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full border border-[#E2E8F0]">
                  <thead>
                    <tr className="bg-[#BDDDFC] text-[#384959]">
                      <th className="p-2">#</th>
                      <th className="p-2">Material Type</th>
                      <th className="p-2">Product</th>
                      <th className="p-2">Product Code</th>
                      <th className="p-2">Product Name</th>
                      <th className="p-2">UOM</th>
                      <th className="p-2">Qty</th>
                      <th className="p-2">Flush Type</th>
                      {/* Department column - Only shown if ShowOnItem is true (matching StockIn implementation) */}
                      {showDepartmentItemField && <th className="p-2">Department</th>}
                      <th className="p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx} className="border-t border-[#E2E8F0]">
                        <td className="p-2 text-center">{idx + 1}</td>
                        <td className="p-2">
                          <select 
                            name="materialType" 
                            value={item.materialType} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-32 border rounded-lg px-2 py-1 ${errors[`materialType_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          >
                            {MATERIAL_TYPE_OPTIONS.map(type => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                          {errors[`materialType_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <SearchableDropdown
                            options={filteredProducts}
                            value={item.productCode}
                            onChange={(productCode) => handleProductChange(idx, productCode)}
                            onMultiSelectTrigger={() => setShowMultiSelectModal(true)}
                            multiSelectShortcut="p"
                            getOptionLabel={(product) => product.ProductName}
                            getOptionValue={(product) => product.ProductCode}
                            getOptionSecondaryLabel={(product) => product.ProductCode}
                            placeholder="Select Product"
                            width="w-40"
                            error={errors[`productCode_${idx}`]}
                            className="data-product-dropdown"
                          />
                          {errors[`productCode_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <div className="flex items-center">
                            <input 
                              name="productCodeDisplay" 
                              value={item.productCode} 
                              readOnly 
                              className="w-24 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                              title="Auto-populated from product selection"
                            />
                            {item.savedToTable && (
                              <span className="saved-item-badge">Saved</span>
                            )}
                          </div>
                        </td>
                        <td className="p-2">
                          <input 
                            name="productName" 
                            value={item.productName} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-40 border rounded-lg px-2 py-1 ${errors[`productName_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                          />
                          {errors[`productName_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="uom" 
                            value={item.uom} 
                            readOnly 
                            className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                            title="Auto-populated from product selection"
                          />
                          {errors[`uom_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="qty" 
                            type="number" 
                            min="0" 
                            step="0.01"
                            value={item.qty} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-16 border rounded-lg px-2 py-1 ${errors[`qty_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                          />
                          {errors[`qty_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <select 
                            name="flushType" 
                            value={item.flushType} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-24 border rounded-lg px-2 py-1 ${errors[`flushType_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          >
                            {FLUSH_TYPE_OPTIONS.map(type => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                          {errors[`flushType_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        
                        {/* Department cell - Only shown if ShowOnItem is true (matching StockIn implementation) */}
                        {showDepartmentItemField && (
                          <td className="p-2">
                            <SearchableDropdown
                              options={departments}
                              value={item.department_id}
                              onChange={(department_id) => {
                                const newItems = [...items];
                                newItems[idx].department_id = department_id;
                                setItems(newItems);
                              }}
                              getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
                              getOptionValue={(dept) => dept.DepartmentID.toString()}
                              placeholder="Select Department"
                              width="w-32"
                              error={errors[`department_id_${idx}`]}
                              className="data-itemdepartment-dropdown"
                            />
                            {errors[`department_id_${idx}`] && (
                              <span className="text-xs text-red-500">*</span>
                            )}
                          </td>
                        )}
                        
                        <td className="p-2 text-center">
                          <button 
                            type="button" 
                            className="text-red-500 hover:text-red-700 transition-colors" 
                            onClick={() => removeItem(idx)} 
                            disabled={items.length === 1}
                          >
                            <TrashIcon />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </form>
        </div>
        
        {/* Fixed Footer with Buttons */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={loading || isSubmitting || !isUserValid}
              onClick={handleSubmit}
            >
              {loading || isSubmitting ? 'Saving...' : (isEdit ? 'Update BOM' : 'Save BOM')}
            </button>
          </div>
        </div>
      </div>
      
      <ProductMultiSelectModal
        open={showMultiSelectModal}
        onClose={() => setShowMultiSelectModal(false)}
        products={dropdownData.products}
        onConfirm={handleMultiSelectProducts}
        onSaveToTable={savePopupItemsToTable}
        onAutofill={autofillSelectedProducts}
      />
    </div>
  );
};

export default BOMFormModal;